import { useEffect, useRef } from 'react';

const days = [
  {
    date: '14',
    month: 'AGO',
    label: 'Quinta-feira',
    title: 'Jantar de Gala',
    description: 'Noite de abertura exclusiva. Conexões que começam antes do evento. Ambiente sofisticado, presença curada.',
    icon: 'ri-goblet-line',
    tag: '1º Dia',
  },
  {
    date: '15',
    month: 'AGO',
    label: 'Sexta-feira',
    title: 'O Evento',
    description: 'Imersão completa. Palestras, painéis e experiências que transformam encontros em marcos.',
    icon: 'ri-mic-line',
    tag: '2º Dia',
    highlight: true,
  },
  {
    date: '16',
    month: 'AGO',
    label: 'Sábado',
    title: 'Roda de Negócios',
    description: 'Conexões estratégicas estruturadas. Mesas temáticas. O objetivo: sair com negócio fechado em 90 dias.',
    icon: 'ri-shake-hands-line',
    tag: '3º Dia',
  },
];

export default function AgendaSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.animate-target').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 140);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="agenda"
      ref={sectionRef}
      className="bg-cream py-24 md:py-32 px-6 relative overflow-hidden"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="animate-target opacity-0 transition-all duration-700 mb-4" style={{ transform: 'translateY(30px)' }}>
            <span className="font-sans text-xs uppercase text-gold" style={{ letterSpacing: '0.35em' }}>
              Agenda Confirmada
            </span>
            <div className="w-12 h-px bg-gold mx-auto mt-3" />
          </div>
          <div className="animate-target opacity-0 transition-all duration-700 mb-3" style={{ transform: 'translateY(30px)' }}>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-obsidian leading-tight">
              São Paulo —{' '}
              <span className="italic text-gold">Alphaville</span>
            </h2>
          </div>
          <div className="animate-target opacity-0 transition-all duration-700" style={{ transform: 'translateY(30px)' }}>
            <p className="font-sans text-sm text-obsidian/50 font-light mt-3" style={{ letterSpacing: '0.05em' }}>
              Agosto de 2026 &nbsp;·&nbsp; 3 dias de experiência
            </p>
          </div>
        </div>

        {/* Timeline / Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 relative">
          {/* Connecting line (desktop) */}
          <div className="hidden md:block absolute top-[52px] left-[16.66%] right-[16.66%] h-px bg-gold/30 z-0" />

          {days.map((day, index) => (
            <div
              key={day.date}
              className="animate-target opacity-0 transition-all duration-700 flex flex-col items-center text-center px-6 md:px-8 relative z-10"
              style={{ transform: 'translateY(30px)', transitionDelay: `${index * 150}ms` }}
            >
              {/* Date circle */}
              <div
                className={`w-24 h-24 flex flex-col items-center justify-center border-2 mb-6 transition-all duration-300 ${
                  day.highlight
                    ? 'border-gold bg-gold text-obsidian'
                    : 'border-gold/50 bg-cream text-obsidian'
                }`}
              >
                <span className={`font-serif text-3xl font-bold leading-none ${day.highlight ? 'text-obsidian' : 'text-obsidian'}`}>
                  {day.date}
                </span>
                <span className={`font-sans text-xs font-semibold mt-0.5 ${day.highlight ? 'text-obsidian/70' : 'text-gold'}`} style={{ letterSpacing: '0.2em' }}>
                  {day.month}
                </span>
              </div>

              {/* Tag */}
              <span className="font-sans text-xs uppercase text-gold/70 mb-2" style={{ letterSpacing: '0.25em' }}>
                {day.tag} &nbsp;·&nbsp; {day.label}
              </span>

              {/* Icon */}
              <div className="w-8 h-8 flex items-center justify-center mb-3">
                <i className={`${day.icon} text-gold text-xl`} />
              </div>

              {/* Title */}
              <h3 className="font-serif text-xl font-bold text-obsidian mb-3">
                {day.title}
              </h3>

              {/* Divider */}
              <div className="w-8 h-px bg-gold mb-4" />

              {/* Description */}
              <p className="font-sans text-sm text-obsidian/55 font-light leading-relaxed">
                {day.description}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom location badge */}
        <div className="animate-target opacity-0 transition-all duration-700 mt-16 flex justify-center" style={{ transform: 'translateY(30px)' }}>
          <div className="flex items-center gap-3 border border-gold/30 px-6 py-3">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-map-pin-line text-gold text-base" />
            </div>
            <span className="font-sans text-xs uppercase text-obsidian/60" style={{ letterSpacing: '0.2em' }}>
              São Paulo &nbsp;·&nbsp; Alphaville &nbsp;·&nbsp; 14, 15 e 16 de Agosto de 2026
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
